import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faCircleInfo, faThumbtack, faCalendarDays, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { Markdown, normalizeMarkdown } from "../shared/markdown";
import { groupReleaseNotesByMajor, parseReleaseDate, flagBadge, effectiveFlag } from "../shared/releaseNotes";
import { isNumericMajorKey, compareVersionsDesc } from "../shared/version";
import type {IdmInfo, ReleaseNote, Version} from "../shared/types/api";
import { useTranslation } from "react-i18next";
import {Modal} from "../shared/Modal";
import { Tooltip } from "../shared/Tooltip";
import { useSettings } from "../shared/hooks/useSettings";

type GroupedReleaseNotes = Record<string, ReleaseNote[]>;
type OpenState = Record<string, boolean>;

function IdMDetail() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const repository = params.get("repository");
    const major = params.get("major");

    const navigate = useNavigate();

    const [latestVersion, setLatestVersion] = useState<Version | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [idm, setIdm] = useState<IdmInfo>();

    const [groupedResults, setGroupedResults] = useState<GroupedReleaseNotes>({});
    const [openMajors, setOpenMajors] = useState<OpenState>({});
    const [openVersions, setOpenVersions] = useState<OpenState>({});
    const [descriptionOpen, setDescriptionOpen] = useState<boolean | null>(null);

    const [recommendedModules, setRecommendedModules] = useState<Version[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

    const { settings } = useSettings();

    // the default open/closed state comes from user settings; explicit toggles win
    const isDescriptionOpen = descriptionOpen ?? settings.descriptionsExpanded;
    const isVersionOpen = (tag: string) => openVersions[tag] ?? settings.descriptionsExpanded;


    useEffect(() => {
        if (!repository || !major) {
            navigate("/");
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const [notes, idmInfo, versions] = await Promise.all([
                    fetchJson<ReleaseNote[]>(
                        buildUrl("/release-note/major", { repository, major })
                    ),
                    fetchJson<IdmInfo>(
                        buildUrl("/idm", { version: major })
                    ),
                    fetchJson<Version[]>(
                        buildUrl("/version/IdM", { repository: repository, major: major })
                    ),
                ]);

                // The latest version is the highest one (the entry at the top of
                // the table below), not just the last element returned by the API.
                const latest = versions && versions.length > 0
                    ? [...versions].sort((a, b) =>
                        compareVersionsDesc(a.tag || a.moduleVersion, b.tag || b.moduleVersion)
                    )[0]
                    : null;

                if (latest) {
                    const latestTag = latest.tag || latest.moduleVersion;
                    const latestNote = notes.find(n => n.tag_name === latestTag);
                    if (latestNote?.released_at) {
                        latest.date = latestNote.released_at;
                    }
                    setLatestVersion(latest);
                } else {
                    setLatestVersion(null);
                }
                // index existing notes by tag_name for fast lookup
                const noteByTag = new Map<string, ReleaseNote>();

                notes.forEach(note => {
                    note.repository = repository;
                    note.description = note.description ?? "";
                    noteByTag.set(note.tag_name, note);
                });

                versions.forEach(v => {
                    const tag = v.tag || v.moduleVersion;
                    if (!tag) return;

                    const existing = noteByTag.get(tag);

                    if (!existing) {
                        // create synthetic note for missing version
                        const synthetic: ReleaseNote = {
                            tag_name: tag,
                            description: "",
                            released_at: v.date,
                            repository: repository,
                            mismatchWithTag: v.mismatchWithTag
                        } as ReleaseNote;

                        notes.push(synthetic);
                        noteByTag.set(tag, synthetic);
                    }
                });

                if (cancelled) return;

                setIdm(idmInfo);

                const groups = groupReleaseNotesByMajor(notes);
                setGroupedResults(groups);

                const expandedMajors: OpenState = {};
                Object.keys(groups).forEach((m) => {
                    expandedMajors[m] = true;
                });
                setOpenMajors(expandedMajors);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : "Neznámá chyba při načítání dat.";
                if (!cancelled) setError(message);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [repository, major, navigate, i18n.language]);

    const handleOpenRecommended = async (idmVersion: string) => {
        setSelectedVersion(idmVersion);

        const response = await
            fetchJson<Version[]>(
                buildUrl("/version/recommendedModules", { idmVersion })
            );

        setRecommendedModules(response);
        setIsModalOpen(true);
    };

    const idmDescription = useMemo(() => {
        return normalizeMarkdown(idm?.description ?? "");
    }, [idm?.description]);

    function getModuleDisplayName(v: Version): string {
        if (settings.showTechnicalNames) {
            return v.nexusName || v.repository;
        }
        return v.tableName || v.repository;
    }

    // The other naming than the displayed one: technical name when business
    // names are shown and vice versa. Null when there is nothing extra to show.
    function getModuleAltName(v: Version): string | null {
        const alt = settings.showTechnicalNames
            ? (v.tableName || v.repository)
            : (v.nexusName || v.repository);
        return alt && alt !== getModuleDisplayName(v) ? alt : null;
    }

    return (
        <div className="page-card">
            <h2 className="page-title">
                <FontAwesomeIcon icon={faFileLines} /> {t("idmDetail.title", { major })}
            </h2>

            {idm && (
                <div className="detail-box detail-box--spaced">
                    <strong className="info-box__title">
                        {idm.description && (
                            <span className="toggle-icon"
                                onClick={() => setDescriptionOpen(!isDescriptionOpen)}
                            >
                                {isDescriptionOpen ? "▼" : "▶"}
                            </span>
                        )}
                        <FontAwesomeIcon icon={faCircleInfo} /> {t("idmDetail.info")}:
                    </strong>

                    {idm.description && isDescriptionOpen && (
                        <div className="info-box__content">
                            {idm.notLocalized && <p className="text-error"><strong>{t("idmDetail.notLocalizedDescription", { language: idm.fallbackLanguage })}</strong></p>}
                            <Markdown>{idmDescription}</Markdown>
                        </div>
                    )}

                    {latestVersion && (
                        <div className="meta-block">
                            <p className="meta-line">
                                <FontAwesomeIcon icon={faThumbtack} /> <strong>{t("idmDetail.latestVersion")}:</strong>{" "}
                                <span className="meta-highlight">
                                    {latestVersion.tag}
                                </span>
                            </p>
                            <p className="meta-line">
                                <FontAwesomeIcon icon={faCalendarDays} /> <strong>{t("idmDetail.releaseDate")}:</strong>{" "}
                                {parseReleaseDate(latestVersion.date)?.toLocaleDateString(i18n.language || "cs-CZ")}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="content-section">
                {isLoading && <p>{t("common.loading")}</p>}
                {error && <p className="text-error">{t("common.error")}: {error}</p>}

                {Object.keys(groupedResults).length > 0 && (
                    <div style={{ marginTop: "30px" }}>
                        <button className="btn-primary"
                            onClick={() => {
                                const collapsedVersions: OpenState = {};
                                Object.keys(groupedResults).forEach((m) => {
                                    groupedResults[m].forEach((rn) => {
                                        collapsedVersions[rn.tag_name] = false;
                                    });
                                });
                                setOpenVersions(collapsedVersions);
                            }}
                        >
                            {t("idmDetail.collapseMinors")}
                        </button>

                        <button className="btn-primary"
                            onClick={() => {
                                const expandedVersions: OpenState = {};
                                Object.keys(groupedResults).forEach((m) => {
                                    groupedResults[m].forEach((rn) => {
                                        expandedVersions[rn.tag_name] = true;
                                    });
                                });
                                setOpenVersions(expandedVersions);
                            }}
                        >
                            {t("idmDetail.expandMinors")}
                        </button>

                        {Object.keys(groupedResults)
                            .sort((a, b) => {
                                const aNumeric = isNumericMajorKey(a);
                                const bNumeric = isNumericMajorKey(b);

                                if (aNumeric && !bNumeric) return -1;
                                if (!aNumeric && bNumeric) return 1;

                                if (aNumeric && bNumeric) {
                                    return parseInt(b, 10) - parseInt(a, 10);
                                }

                                return a.localeCompare(b);
                            })
                            .map((majorKey) => (
                                <div key={majorKey} style={{ marginBottom: "20px" }}>
                                    <div className="major-header">
                                        {t("idmDetail.majorHeader", { major: majorKey, count: groupedResults[majorKey].length })}
                                    </div>

                                    {openMajors[majorKey] && (
                                        <div className="major-content">
                                            {groupedResults[majorKey].map((rn) => (
                                                <div key={rn.tag_name} className="release-wrapper">
                                                    <div className="release-item">
                                                        <span className="toggle-icon"
                                                            onClick={() =>
                                                                setOpenVersions(prev => ({
                                                                    ...prev,
                                                                    [rn.tag_name]: !isVersionOpen(rn.tag_name),
                                                                }))
                                                            }
                                                        >
                                                            {isVersionOpen(rn.tag_name) ? "▼" : "▶"}
                                                        </span>

                                                        <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                                                            {rn.tag_name}
                                                        </span>

                                                        {flagBadge(effectiveFlag(rn.flag, rn.tag_name)) && (
                                                            <span className="badge release-flag" style={{ marginRight: "10px" }}>
                                                                {flagBadge(effectiveFlag(rn.flag, rn.tag_name))}
                                                            </span>
                                                        )}

                                                        <span className="badge">
                                                          <FontAwesomeIcon icon={faCalendarDays} /> {parseReleaseDate(rn.released_at)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                                                        </span>

                                                        <span>
                                                            <button
                                                                className="icon-button"
                                                                onClick={() => handleOpenRecommended(rn.tag_name)}
                                                                title={t("idmDetail.recommendedModule")}
                                                            >
                                                                <FontAwesomeIcon icon={faMagnifyingGlass} /> {t("idmDetail.recommendedModule")}
                                                            </button>
                                                        </span>

                                                        {isVersionOpen(rn.tag_name) && (
                                                            <div className="detail-box detail-box--spaced">
                                                                {rn?.notLocalized && <p className="text-error"><strong>{t("idmDetail.notLocalized", { language: rn.fallbackLanguage})}</strong></p> }
                                                                <p>
                                                                    <strong>{t("idmDetail.releaseDate")}</strong>{" "}
                                                                    {parseReleaseDate(rn.released_at)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                                                                </p>

                                                                <div className="detail-box">
                                                                    <Markdown>
                                                                        {normalizeMarkdown(rn.description || "") || t("idmDetail.noDescription")}
                                                                    </Markdown>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                )}
            </div>
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`${t("idmDetail.recommendedModule")} ${selectedVersion}`}
            >
                <div className="table-wrapper">
                    <table>
                        <thead>
                        <tr>
                            <th>{t("modules.table.moduleName")}</th>
                            <th>{t("modules.table.latestVersion")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {[...recommendedModules]
                            .sort((a, b) =>
                                getModuleDisplayName(a).localeCompare(getModuleDisplayName(b), i18n.language || "cs-CZ", { sensitivity: "base" })
                            )
                            .map((m, i) => {
                            const altName = getModuleAltName(m);
                            return (
                                <tr key={i}>
                                    <td>
                                        {altName ? (
                                            <Tooltip label={altName}>
                                                {getModuleDisplayName(m)}
                                            </Tooltip>
                                        ) : (
                                            getModuleDisplayName(m)
                                        )}
                                    </td>
                                    <td>{m.moduleVersion}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </div>
    );
}

export default IdMDetail;